/**
 * GPU particles on TSL compute (Phase 8): three pools, each a set of per-particle vec4 storage
 * buffers the CPU writes to spawn (position and birth stamp, velocity and life, size and motion
 * parameters, colour and fade curve) and the GPU integrates every frame in a kernel (spawn when
 * the birth stamp changed, else gravity, drag, a floor bounce for debris). The materials read the
 * integrated state as instanced attributes, so no readback ever happens. The additive pool draws
 * flashes, sparks, fire, embers, jets and shards; the smoke pool draws smoke, steam and dust
 * with normal blending; the debris pool draws small opaque chunks with a real G-buffer. Three
 * draw calls in all. The kernel runs on WebGPU; the WebGL 2 backend's transform feedback
 * cannot bind the five buffers the kernel touches (four varyings at most), so that backend, a
 * backend that refuses the kernel, and `?particles=cpu` step the same buffers on the CPU
 * (cpu.ts), which draws through the very same materials.
 */
import {
  AdditiveBlending,
  BoxGeometry,
  Mesh,
  MeshBasicNodeMaterial,
  NormalBlending,
  PlaneGeometry,
  SpriteNodeMaterial,
  type ComputeNode,
  type Material,
  type Scene,
  type WebGPURenderer,
} from 'three/webgpu';
import {
  Fn,
  If,
  float,
  instanceIndex,
  instancedArray,
  positionGeometry,
  rotate,
  select,
  smoothstep,
  uniform,
  uv,
  vec3,
  vec4,
} from 'three/tsl';

/** A TSL node with the swizzles and operators every TSL value carries. */
type AnyNode = ReturnType<typeof vec4>;
/** A float uniform as a TSL value (operators) with its CPU-side value. */
type UniformFloat = ReturnType<typeof float> & { value: number };

import { effectMrt } from '../mrt.js';
import {
  aliveCount,
  BOUNCE_FRICTION,
  BOUNCE_RESTITUTION,
  createParticleBuffers,
  FLOOR_Y,
  stepParticlesCpu,
  type ParticleBuffers,
} from './cpu.js';
import type { ParticleSpec, PoolName } from './emitters.js';

export type ParticleMode = 'gpu' | 'cpu';

export interface PoolCapacities {
  additive: number;
  smoke: number;
  debris: number;
}

export const DEFAULT_CAPACITIES: PoolCapacities = { additive: 2048, smoke: 1024, debris: 256 };

export interface ParticlePoolStats {
  capacity: number;
  alive: number;
}

export interface ParticleStats {
  mode: ParticleMode;
  pools: Record<PoolName, ParticlePoolStats>;
  /** Particles emitted since the match started. */
  emitted: number;
}

interface StorageNode {
  value: { array: Float32Array; needsUpdate: boolean };
  element(index: unknown): AnyNode;
  toAttribute(): AnyNode;
}

class Pool {
  readonly buffers: ParticleBuffers;
  readonly mesh: Mesh;
  readonly kernel: ComputeNode;
  private cursor = 0;
  private dirty = false;
  private readonly material: Material;
  private readonly emitA: StorageNode;
  private readonly emitB: StorageNode;
  private readonly emitC: StorageNode;
  private readonly emitD: StorageNode;
  private readonly stateA: StorageNode;
  private readonly stateB: StorageNode;

  constructor(
    readonly name: PoolName,
    readonly capacity: number,
    private readonly uNow: UniformFloat,
    private readonly uDt: UniformFloat,
  ) {
    this.buffers = createParticleBuffers(capacity);
    const b = this.buffers;
    // instancedArray builds a StorageInstancedBufferAttribute (a vertex attribute on the WebGL
    // backend); its typed array is replaced by ours so the CPU writes land without copies.
    const wrap = (data: Float32Array): StorageNode => {
      const node = instancedArray(capacity, 'vec4') as unknown as StorageNode;
      node.value.array = data;
      return node;
    };
    this.emitA = wrap(b.emitA);
    this.emitB = wrap(b.emitB);
    this.emitC = wrap(b.emitC);
    this.emitD = wrap(b.emitD);
    this.stateA = wrap(b.stateA);
    this.stateB = wrap(b.stateB);
    this.kernel = this.buildKernel();
    this.material = this.buildMaterial();
    // A plain mesh drawn `count` times: no instance-matrix attribute, which keeps the vertex
    // buffers under WebGPU's limit of eight (geometry plus the four particle buffers read).
    this.mesh = new Mesh(
      name === 'debris' ? new BoxGeometry(1, 0.6, 1.4) : new PlaneGeometry(1, 1),
      this.material,
    );
    this.mesh.count = capacity;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.renderOrder = name === 'smoke' ? 10 : name === 'additive' ? 20 : 0;
  }

  private buildKernel(): ComputeNode {
    const { emitA, emitB, emitC, stateA, stateB } = this;
    const now = this.uNow;
    const dt = this.uDt;
    const step = Fn(() => {
      const i = instanceIndex;
      const ea = emitA.element(i);
      const eb = emitB.element(i);
      const ec = emitC.element(i);
      const sa = stateA.element(i);
      const sb = stateB.element(i);
      If(sa.w.notEqual(ea.w), () => {
        // A new birth stamp: the CPU spawned into this slot.
        sa.assign(ea);
        sb.assign(vec4(eb.xyz, 0.0));
      }).Else(() => {
        const age = now.sub(ea.w);
        If(age.lessThan(eb.w), () => {
          const vel = sb.xyz.toVar();
          vel.y.addAssign(ec.y.mul(dt));
          vel.mulAssign(float(1.0).sub(ec.z.mul(dt)).max(0.0));
          const pos = sa.xyz.add(vel.mul(dt)).toVar();
          If(ec.w.greaterThan(0.5).and(pos.y.lessThan(FLOOR_Y)).and(vel.y.lessThan(0.0)), () => {
            pos.y.assign(FLOOR_Y);
            vel.y.assign(vel.y.negate().mul(BOUNCE_RESTITUTION));
            vel.x.mulAssign(BOUNCE_FRICTION);
            vel.z.mulAssign(BOUNCE_FRICTION);
          });
          sa.assign(vec4(pos, ea.w));
          sb.assign(vec4(vel, age));
        });
      });
    });
    return step().compute(this.capacity);
  }

  private buildMaterial(): SpriteNodeMaterial | MeshBasicNodeMaterial {
    // Four particle buffers: the integrated position with its birth stamp, the life, the size
    // and the colour with the fade curve (the spawn position and velocity are the kernel's).
    const pos = this.stateA.toAttribute();
    const eb = this.emitB.toAttribute();
    const ec = this.emitC.toAttribute();
    const ed = this.emitD.toAttribute();
    const born = pos.w;
    const life = eb.w;
    const age = this.uNow.sub(born);
    const t = age.div(life.max(0.001)).clamp(0.0, 1.0);
    const alive = born.greaterThan(0.0).and(age.greaterThanEqual(0.0)).and(age.lessThan(life));
    const size = ec.x;
    if (this.name === 'debris') {
      const material = new MeshBasicNodeMaterial();
      const spin = rotate(positionGeometry, vec3(age.mul(3.0), age.mul(2.0), 0.0));
      material.positionNode = spin.mul(select(alive, size, 0.0)).add(pos.xyz);
      material.colorNode = ed.rgb.mul(0.45);
      return material;
    }
    const material = new SpriteNodeMaterial();
    material.positionNode = pos.xyz;
    // fade 0: bright then out, slight swell; fade 1: swell and thin (smoke).
    const swell = select(
      ed.w.greaterThan(0.5),
      float(0.5).add(t.mul(1.5)),
      float(1.0).add(t.mul(0.5)),
    );
    material.scaleNode = select(alive, size.mul(swell), 0.0);
    const d = uv().sub(0.5).length();
    const soft = smoothstep(0.5, 0.12, d);
    if (this.name === 'additive') {
      const fade = float(1.0).sub(t);
      material.colorNode = ed.rgb.mul(fade.mul(fade)).mul(soft).mul(2.2);
      material.blending = AdditiveBlending;
    } else {
      const opacity = float(1.0).sub(t).mul(t.mul(6.0).min(1.0)).mul(0.38);
      material.colorNode = ed.rgb;
      material.opacityNode = opacity.mul(soft);
      material.blending = NormalBlending;
    }
    material.transparent = true;
    material.depthWrite = false;
    material.mrtNode = effectMrt();
    return material;
  }

  /** Writes one particle into the ring; the oldest slot is reused when the pool is full. */
  emit(s: ParticleSpec, now: number): void {
    const b = this.buffers;
    const o = this.cursor * 4;
    this.cursor = (this.cursor + 1) % this.capacity;
    b.emitA[o] = s.x;
    b.emitA[o + 1] = s.y;
    b.emitA[o + 2] = s.z;
    b.emitA[o + 3] = now;
    b.emitB[o] = s.vx;
    b.emitB[o + 1] = s.vy;
    b.emitB[o + 2] = s.vz;
    b.emitB[o + 3] = s.life;
    b.emitC[o] = s.size;
    b.emitC[o + 1] = s.gravity;
    b.emitC[o + 2] = s.drag;
    b.emitC[o + 3] = s.bounce ? 1 : 0;
    b.emitD[o] = s.r;
    b.emitD[o + 1] = s.g;
    b.emitD[o + 2] = s.b;
    b.emitD[o + 3] = s.fade;
    this.dirty = true;
  }

  /** Uploads this frame's spawns; the GPU or the CPU then integrates. */
  flush(): void {
    if (!this.dirty) return;
    this.dirty = false;
    this.emitA.value.needsUpdate = true;
    this.emitB.value.needsUpdate = true;
    this.emitC.value.needsUpdate = true;
    this.emitD.value.needsUpdate = true;
  }

  stepCpu(now: number, dt: number): void {
    stepParticlesCpu(this.buffers, now, dt);
    this.stateA.value.needsUpdate = true;
    this.stateB.value.needsUpdate = true;
  }

  stats(now: number): ParticlePoolStats {
    return { capacity: this.capacity, alive: aliveCount(this.buffers, now) };
  }

  clear(): void {
    this.buffers.emitA.fill(0);
    this.buffers.emitB.fill(0);
    this.dirty = true;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}

export class ParticleSystem {
  private readonly uNow = uniform(0) as unknown as UniformFloat;
  private readonly uDt = uniform(0) as unknown as UniformFloat;
  private readonly pools: Record<PoolName, Pool>;
  private mode: ParticleMode;
  private emitted = 0;
  private now = 0;
  private failures = 0;

  constructor(
    private readonly scene: Scene,
    mode: ParticleMode = 'gpu',
    capacities: PoolCapacities = DEFAULT_CAPACITIES,
  ) {
    this.mode = mode;
    this.pools = {
      additive: new Pool('additive', capacities.additive, this.uNow, this.uDt),
      smoke: new Pool('smoke', capacities.smoke, this.uNow, this.uDt),
      debris: new Pool('debris', capacities.debris, this.uNow, this.uDt),
    };
    for (const pool of Object.values(this.pools)) scene.add(pool.mesh);
  }

  get currentMode(): ParticleMode {
    return this.mode;
  }

  emit(specs: readonly ParticleSpec[], now: number): void {
    for (const s of specs) {
      this.pools[s.pool].emit(s, now);
      this.emitted++;
    }
  }

  /**
   * Runs before the frame renders: uploads the spawns and integrates every pool, on the GPU
   * through the compute kernels or on the CPU when the backend refused them.
   */
  update(renderer: WebGPURenderer | null, now: number, dt: number): void {
    this.now = now;
    const step = Math.min(dt, 0.05);
    this.uNow.value = now;
    this.uDt.value = step;
    for (const pool of Object.values(this.pools)) pool.flush();
    if (this.mode === 'gpu' && renderer) {
      try {
        void renderer.compute([
          this.pools.additive.kernel,
          this.pools.smoke.kernel,
          this.pools.debris.kernel,
        ]);
        return;
      } catch (error) {
        // Fall back for good: the same buffers stepped on the CPU, nothing else changes.
        this.failures++;
        this.mode = 'cpu';
        console.warn('particles: compute kernel refused, stepping on the CPU', error);
      }
    }
    for (const pool of Object.values(this.pools)) pool.stepCpu(now, step);
  }

  stats(): ParticleStats {
    return {
      mode: this.mode,
      emitted: this.emitted,
      pools: {
        additive: this.pools.additive.stats(this.now),
        smoke: this.pools.smoke.stats(this.now),
        debris: this.pools.debris.stats(this.now),
      },
    };
  }

  /** Every particle gone (a round starts). */
  clear(): void {
    for (const pool of Object.values(this.pools)) pool.clear();
  }

  dispose(): void {
    for (const pool of Object.values(this.pools)) {
      this.scene.remove(pool.mesh);
      pool.dispose();
    }
  }
}
